import { MutableRefObject } from "react";

import { useQueryCallbacks } from "@/hooks/useQueryCallbacks";
import { useGetIdentificationStatus } from "@/services/api";
import {
  IdentificationStatusResponse,
  IdentificationStatusResponseKycStatus,
} from "@/services/model";

const IDENTIFICATION_STATUS_CACHE_TIME_MS = 5 * 60 * 1000;
const IDENTIFICATION_STATUS_VERIFIED_STALE_TIME_MS = 5 * 60 * 1000;
const IDENTIFICATION_STATUS_FRAUD_CHECK_POLL_MS = 5_000;

type IdentificationStatusStaleTimeRef = MutableRefObject<number>;

export const createIdentificationStatusQueryOptions = (
  staleTimeRef: IdentificationStatusStaleTimeRef,
) => ({
  refetchOnWindowFocus: true,
  refetchOnMount: true,
  gcTime: IDENTIFICATION_STATUS_CACHE_TIME_MS,
  retry: false,
  staleTime: staleTimeRef.current,
  refetchInterval: ({
    state: { data },
  }: {
    state: { data?: IdentificationStatusResponse };
  }) =>
    data?.kycStatus ===
    IdentificationStatusResponseKycStatus.FRAUD_CHECK_PENDING
      ? IDENTIFICATION_STATUS_FRAUD_CHECK_POLL_MS
      : false,
});

/**
 * The identification status with the options above, which also extend the
 * stale time once the customer is verified.
 */
export const useIdentificationStatus = (
  staleTimeRef: IdentificationStatusStaleTimeRef,
) => {
  const query = useGetIdentificationStatus({
    query: createIdentificationStatusQueryOptions(staleTimeRef),
  });
  useQueryCallbacks(query, {
    onSuccess: (data) => {
      if (
        data?.kycStatus === IdentificationStatusResponseKycStatus.OK &&
        data?.isVerified
      ) {
        staleTimeRef.current = IDENTIFICATION_STATUS_VERIFIED_STALE_TIME_MS;
      }
    },
  });

  return query;
};
